// src/pages/Contact.js
import React, { useState, useEffect, useCallback } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import { useSettings } from '../context/SettingsContext';
import { FaFacebook, FaInstagram, FaTiktok, FaSnapchat, FaReddit, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

function Contact() {
  const { settings } = useSettings();
  const [isSmallDevice, setIsSmallDevice] = useState(window.innerWidth < 768);
  const [name, setName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsSmallDevice(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = useCallback(async (e) => {
      e.preventDefault(); if (isSubmitting) return; setIsSubmitting(true);
      setSuccessMsg(''); setErrorMsg('');
      if (!name || !userEmail || !message) { setErrorMsg('Please fill out all required fields'); setIsSubmitting(false); return; }
      try {
          const response = await fetch('/api/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email: userEmail, phone, message }), });
          const data = await response.json();
          if (response.ok && data.success) { setSuccessMsg('Message sent successfully!'); setName(''); setUserEmail(''); setPhone(''); setMessage(''); }
          else { setErrorMsg(data.error || `Request failed: ${response.status}`); }
      } catch (err) { console.error('Contact form error:', err); setErrorMsg('Could not send message.'); }
      finally { setIsSubmitting(false); }
  }, [name, userEmail, phone, message, isSubmitting]);

  // --- Style Definitions ---

  // Main page wrapper - Uses Flexbox
  const pageWrapperStyle = {
      display: 'flex',
      flexDirection: isSmallDevice ? 'column' : 'row',
      // height: '100%', // REMOVE THIS: Let content define height
      backgroundColor: '#fff',
      // overflow: 'hidden', // REMOVE THIS: Allow scrolling
  };

  // Animation column:  Takes right 50% on desktop, height from content
    const animationContainerStyle = {
        flexBasis: isSmallDevice ? '0' : '50%',
        display: isSmallDevice ? 'none' : 'block',
        flexShrink: 0,
        position: 'relative', //  Important: For absolute positioning of the canvas inside
        // height: isSmallDevice ? 'auto' : '100vh', // Remove this, let content column define height
        overflow: 'hidden',
    };

  // Content column: Takes left 50% on desktop, full on mobile
  const contentContainerStyle = {
      flexBasis: isSmallDevice ? '100%' : '50%',
      flexShrink: 0,
      padding: '2rem 1.5rem 3rem 1.5rem',
      backgroundColor: '#fff',
      zIndex: 1,
  };

  // Button styles - Yellow CTA accent
  const bwButtonStyle = {
      display: 'inline-block',
      background: '#FFC107',
      color: '#6c757d',
      border: '2px solid #FFC107',
      borderRadius: '8px',
      padding: '0.75rem 2rem',
      cursor: 'pointer',
      fontSize: '1rem',
      textDecoration: 'none',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(51, 51, 51, 0.1)',
  };
  const bwButtonStyleHover = {
      background: '#e0a800',
      color: '#6c757d',
      borderColor: '#e0a800',
      boxShadow: '0 4px 8px rgba(51, 51, 51, 0.15)',
      transform: 'translateY(-2px)',
  };
  const submitButtonStyle = {
      ...bwButtonStyle,
      ...(isSubmitHovered && !isSubmitting ? bwButtonStyleHover : {}),
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      opacity: isSubmitting ? 0.7 : 1,
  };

  // Other needed styles...
  const labelStyle = { fontWeight: 'bold', marginBottom: '0.3rem', display: 'block', color: '#007BFF' };
  const inputStyle = { display: 'block', width: '100%', padding: '.375rem .75rem', fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#6c757d', backgroundColor: '#fff', backgroundClip: 'padding-box', border: '2px solid #007BFF', appearance: 'none', borderRadius: '6px', transition: 'all .15s ease-in-out', marginBottom: '1rem' };
  const textAreaStyle = { ...inputStyle, height: 'auto', minHeight: 'calc(1.5em + .75rem + 2px)' };
  const infoListStyle = { margin: 0, padding: 0, listStyle: 'none' };
  const infoRowStyle = { display: 'flex', marginBottom: '0.75rem', lineHeight: '1.5', flexWrap: 'wrap' };
  infoRowStyle.dt = { fontWeight: 'bold', width: '100px', flexShrink: 0, color: '#007BFF', paddingRight: '10px', };
  infoRowStyle.dd = { margin: 0, color: '#6c757d', flexGrow: 1, };
  const alertBaseStyle = { padding: '1rem', marginBottom: '1rem', border: '1px solid transparent', borderRadius: '4px', textAlign: 'center', fontWeight: '500', };
  const alertSuccessStyle = { ...alertBaseStyle, color: '#6c757d', backgroundColor: '#e9f7ef', borderColor: '#a9dbc0', };
  const alertErrorStyle = { ...alertBaseStyle, color: '#6c757d', backgroundColor: '#f8d7da', borderColor: '#f5c6cb', };

  return (
    // Main Flex container
    <div style={pageWrapperStyle}>
       {/* Content Area (Left half on Desktop, Full on Mobile) */}
       <div style={contentContainerStyle}>
          <h1 style={{ color: '#007BFF' }}>Contact Kentech Phones</h1>
          <p style={{ color: '#6c757d' }}>
              Have a question, need a quote, or want to book a service?
              Use the form below or contact us directly.
          </p>

          {successMsg && <div style={alertSuccessStyle}>{successMsg}</div>}
          {errorMsg && <div style={alertErrorStyle}>{errorMsg}</div>}

          <section className="contact-form-section mt-4">
          <h2 style={{ color: '#007BFF' }}>Send Us a Message</h2>
          <form onSubmit={handleSubmit}>
              {/* Form fields remain exactly the same */}
              <div className="mb-3">
                  <label htmlFor="name" style={labelStyle}>Name:</label>
                  <input type="text" id="name" placeholder="Enter your name" style={inputStyle} required value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="mb-3">
                 <label htmlFor="email" style={labelStyle}>Email:</label>
                 <input type="email" id="email" placeholder="Enter your email" style={inputStyle} required value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
               </div>
               <div className="mb-3">
                 <label htmlFor="phone" style={labelStyle}>Phone:</label>
                 <input type="tel" id="phone" placeholder="Enter your phone number" style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} />
               </div>
               <div className="mb-3">
                 <label htmlFor="message" style={labelStyle}>Message:</label>
                 <textarea id="message" rows="5" placeholder="Describe your issue or question" style={textAreaStyle} required value={message} onChange={(e) => setMessage(e.target.value)} />
               </div>

              {/* Submit Button with Hover State - No changes needed here */}
              <button type="submit" style={submitButtonStyle} disabled={isSubmitting} onMouseEnter={() => setIsSubmitHovered(true)} onMouseLeave={() => setIsSubmitHovered(false)} >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
          </form>
          </section>

          <section className="contact-info mt-5">
          <h2 style={{ color: '#007BFF' }}>Other Ways to Reach Us</h2>
          <dl style={infoListStyle}>
               {/* Contact info rows remain the same */}
               <div style={infoRowStyle}><dt style={infoRowStyle.dt}>Phone:</dt><dd style={infoRowStyle.dd}>07944 859580</dd></div>
               <div style={infoRowStyle}><dt style={infoRowStyle.dt}>Email:</dt><dd style={infoRowStyle.dd}>support@kentechphonerepairs.co.uk</dd></div>
               <div style={infoRowStyle}><dt style={infoRowStyle.dt}>Address:</dt><dd style={infoRowStyle.dd}>24-25 New Rd,<br/>Gravesend DA11 0AA</dd></div>
               <div style={infoRowStyle}>
                 <dt style={infoRowStyle.dt}>Hours:</dt>
                 <dd style={infoRowStyle.dd}>
                   <strong>Available:</strong> 24/7<br/>
                   <strong>Contact us at:</strong> 9am to 9pm
                 </dd>
               </div>
          </dl>
          </section>
      </div>
      {/* Animated Background (Right half on Desktop) */}
      {!isSmallDevice && (
        <div style={animationContainerStyle}>
          {/* AnimatedBackground needs to fill this container */}
          <AnimatedBackground />
        </div>
      )}

     

    </div> // End page wrapper
  );
}

export default Contact;